import { Box, Button, TextField, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";

const skipInk = { shouldForwardProp: (prop: PropertyKey) => prop !== "$ink" };

interface IInkProps {
  $ink?: boolean;
}

export const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export const FieldLabel = styled(
  "label",
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  fontSize: 13,
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
  color: $ink ? theme.palette.common.white : theme.palette.text.primary,
}));

export const EmailField = styled(
  TextField,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: theme.shape.borderRadius,
    ...($ink && {
      backgroundColor: theme.palette.common.white,
      color: "#111111",
    }),

    "&.Mui-focused:not(.Mui-error)": {
      boxShadow: "0 0 0 3px rgba(245, 183, 0, 0.28)",

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#DBA400",
        borderWidth: 1,
      },
    },

    "&.Mui-error": {
      boxShadow: "0 0 0 3px rgba(211, 47, 47, 0.14)",
    },
  },

  "& .MuiInputBase-readOnly": {
    backgroundColor:
      theme.palette.mode === "dark" && !$ink ? undefined : "#FAFAFA",
  },
}));

export const SubmitButton = styled(
  Button,
  skipInk
)<IInkProps>(({ theme, $ink }) => {
  const isDark = theme.palette.mode === "dark";
  const restingBackground = $ink
    ? "#F5B700"
    : isDark
      ? theme.palette.common.white
      : "#111111";
  const restingColor = $ink
    ? "#0B0C0E"
    : isDark
      ? "#111111"
      : theme.palette.common.white;
  const hoverBackground = $ink
    ? "#DBA400"
    : isDark
      ? theme.palette.grey[300]
      : "#2A2E35";

  return {
    height: 44,
    borderRadius: theme.shape.borderRadius,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "none",
    backgroundColor: restingBackground,
    color: restingColor,

    "&:hover": {
      boxShadow: "none",
      backgroundColor: hoverBackground,
    },

    "&.Mui-disabled": {
      backgroundColor: "#2A2E35",
      color: theme.palette.common.white,
    },

    "& .MuiCircularProgress-root": {
      color: "inherit",
    },
  };
});

export const Microcopy = styled(
  Typography,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  fontSize: 12,
  color: $ink ? "rgba(255, 255, 255, 0.6)" : theme.palette.text.secondary,
}));

export const SuccessRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
}));

export const SuccessIconTile = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha("#26BD6C", 0.14),
  color: "#26BD6C",
}));

export const SuccessTitle = styled(
  Typography,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  fontSize: 18,
  fontWeight: 700,
  color: $ink ? theme.palette.common.white : theme.palette.text.primary,
}));

export const SuccessBody = styled(
  Typography,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  fontSize: 14,
  color: $ink ? "rgba(255, 255, 255, 0.7)" : theme.palette.text.secondary,
}));

export const BackButton = styled(
  Button,
  skipInk
)<IInkProps>(({ theme, $ink }) => ({
  height: 40,
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  fontSize: 14,
  fontWeight: 600,
  color: $ink ? theme.palette.common.white : theme.palette.text.primary,
  borderColor: $ink ? "rgba(255, 255, 255, 0.7)" : theme.palette.divider,
}));
